import React, { memo, useEffect, useState } from 'react'

import { Icon } from '@clientbase/clientbase-library'
import { faBars } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Box,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  useMediaQuery
} from '@mui/material'
import Typography from '@mui/material/Typography'
import { DataService } from 'api/DataService'
import ChangelogBellButton from 'features/Changelog/components/ChangelogBellButton'
import { usePermission } from 'hooks/usePermission'
import { isGroupMode } from 'lib/merchant-group'

import Can from 'components/Can'
import ChatAssistant from 'components/ChatAssistant'
import Logo from 'components/Logo'
import { ModalBalance } from 'components/Modal/ModalBalance'
import ModalReport from 'components/Modal/ModalReport'
import NotificationCenter from 'components/NotificationCenter'

import { formatMoney } from 'utils/FormatMoney'
import { OPEN_REPORT_MODAL_EVENT } from 'utils/report-modal-event'

import { theme } from 'styles/theme'

import * as S from './AppBar.styles'
import Help from './components/Help'
import MerchantAccount from './components/MerchantAccount'
import MobileDropdown from './components/MobileDropdown'
import NPSButton from './components/NPSButton'
import QuickActionButton from './components/QuickActionButton'
import Search from './components/Search'
import Shortcuts from './components/Shortcuts'

type AppBarProps = {
  showTopGlobalInfo?: boolean
  setDrawerIsOpen: (drawerIsOpen: boolean) => void
  isExpandedDrawer: boolean
  setIsExpandedMobileMenu: React.Dispatch<React.SetStateAction<boolean>>
  isExpandedMobileMenu: boolean
}

const AppBar = ({
  showTopGlobalInfo,
  isExpandedMobileMenu,
  setIsExpandedMobileMenu,
  isExpandedDrawer
}: AppBarProps) => {
  const [refresh, setRefresh] = useState(false)
  const [isOpenModalReport, setIsOpenModalReport] = useState(false)

  const [isAssistantModalOpen, setIsAssistantModalOpen] = useState(false)
  const [balance, setBalance] = useState<false | number>(false)
  const [balanceScheduled, setBalanceScheduled] = useState<false | number>(
    false
  )
  const canViewBalance = usePermission('balance:view')
  const groupMode = isGroupMode()
  // Indicador de contestação pendente vem embutido na própria chamada de saldo
  // (/v1/merchants/balance), evitando uma requisição dedicada de disputes em
  // toda página.
  const [hasActiveDisputes, setHasActiveDisputes] = useState(false)
  const [hiddedBalance, setHiddenBalance] = useState(false)
  const [modalDetailsIsOpen, setModalDetailsIsOpen] = useState(false)
  const [balanceInitialTab, setBalanceInitialTab] = useState<
    string | undefined
  >(undefined)
  const [balanceInitialDisputeUuid, setBalanceInitialDisputeUuid] = useState<
    string | null
  >(null)
  const refreshTable = () => setRefresh((s) => !s)
  const match = useMediaQuery('(max-width: 770px)', { noSsr: false })

  const handleOpenMenuDetails = () => {
    setBalanceInitialTab(undefined)
    setBalanceInitialDisputeUuid(null)
    setModalDetailsIsOpen(true)
  }

  const handleCloseModalDetails = () => {
    setModalDetailsIsOpen(false)
    setBalanceInitialTab(undefined)
    setBalanceInitialDisputeUuid(null)
    refreshTable()
  }

  const handleOpenBalanceModal = (tab: string, disputeUuid?: string) => {
    setBalanceInitialTab(tab)
    setBalanceInitialDisputeUuid(disputeUuid ?? null)
    setModalDetailsIsOpen(true)
  }

  const handleToggleMobileMenu = () => setIsExpandedMobileMenu((s) => !s)

  const handleBalace = async () => {
    const res = await DataService({
      type: 'GET',
      url: '/v1/merchants/balance'
    })

    // Em queda de rede o DataService devolve a resposta sem `data`
    if (!res?.data) return

    setBalance(Number(res.data.balance))
    setBalanceScheduled(Number(res.data.balance_scheduled))
    setHasActiveDisputes(Boolean(res.data.has_active_disputes))
  }
  useEffect(() => {
    if (canViewBalance && !groupMode) {
      handleBalace()
    }
  }, [refresh, canViewBalance])

  // Relatórios não têm mais tela própria — este modal é o único acesso. Quem
  // está fora da árvore do AppBar (rotas antigas, notificação in-app, filtros
  // de outras telas) pede a abertura por evento de janela.
  useEffect(() => {
    const handleOpenReportModal = () => setIsOpenModalReport(true)

    window.addEventListener(OPEN_REPORT_MODAL_EVENT, handleOpenReportModal)
    return () =>
      window.removeEventListener(OPEN_REPORT_MODAL_EVENT, handleOpenReportModal)
  }, [])

  return (
    <>
      <S.AppBarMUI
        showTopGlobalInfo={showTopGlobalInfo}
        position="fixed"
        isExpandedDrawer={isExpandedDrawer}
        isExpandedMobileMenu={match && isExpandedMobileMenu}
      >
        <S.Toolbar isExpandedMobileMenu={match && isExpandedMobileMenu}>
          {!match && (
            <Box
              display="flex"
              alignItems="end"
              gap="22px"
              minWidth="30%"
              width="100%"
              mr={3}
            >
              <Search />
              <Divider
                sx={{
                  borderColor: theme.palette.neutral[500],
                  margin: '8px 0px'
                }}
                flexItem
                orientation="vertical"
              />
              <Shortcuts />
            </Box>
          )}
          {!match ? (
            <S.ToolbarContentWrapper>
              {/* <Weather /> - Comentado em 17/10/2023 */}
              {/* SC-10999: saldo, saque e antecipação são de cada empresa;
                  em "Todas as empresas" o saldo seria só o da empresa de
                  contexto */}
              {!groupMode && (
                <>
                  <Can permission="balance:view">
                    <Box display="flex" alignItems="center" gap="6px">
                      <Tooltip title="Visualizar Detalhes">
                        <S.BalanceButton
                          variant="text"
                          onClick={handleOpenMenuDetails}
                        >
                          <Box display="flex" alignItems="center" gap="3px">
                            <Typography
                              color={theme.palette.neutral[400]}
                              variant="xsLight"
                            >
                              Saldo
                            </Typography>
                            {hasActiveDisputes && (
                              <Icon
                                icon="error"
                                sx={{
                                  color: theme.palette.error[300],
                                  fontSize: '0.9rem'
                                }}
                              />
                            )}
                          </Box>

                          {balance === false && (
                            <Skeleton
                              variant="rectangular"
                              width={64}
                              height={23}
                              sx={{ borderRadius: 2 }}
                            />
                          )}

                          {balance !== false && (
                            <Typography
                              sx={{
                                whiteSpace: 'nowrap',
                                display: 'flex'
                              }}
                              onClick={handleOpenMenuDetails}
                              variant="smBold"
                              color={theme.palette.neutral[400]}
                            >
                              {hiddedBalance
                                ? 'R$ *****'
                                : formatMoney(Number(balance))}
                            </Typography>
                          )}
                        </S.BalanceButton>
                      </Tooltip>
                      <Tooltip
                        enterTouchDelay={0}
                        title={
                          hiddedBalance ? 'Mostrar saldo' : 'Esconder saldo'
                        }
                      >
                        <IconButton
                          onClick={() => setHiddenBalance(!hiddedBalance)}
                        >
                          <Icon
                            colorSX={theme.palette.neutral[400]}
                            icon={
                              hiddedBalance ? 'visibilityOff' : 'visibility'
                            }
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Can>
                  <Divider
                    sx={{
                      borderColor: theme.palette.neutral[500]
                    }}
                    flexItem={true}
                    orientation="vertical"
                  />
                </>
              )}
              <S.EndIconsWrapper>
                {/* SC-10999: o assistente responde sobre uma empresa; no grupo
                    ele fica fora, como na Visão Geral */}
                {!groupMode && (
                  <S.AssistantButton
                    variantButton="primary"
                    onClick={() => setIsAssistantModalOpen(true)}
                  >
                    <Icon
                      icon="autoAwesome"
                      colorSX={theme.palette.primary.main}
                    />
                  </S.AssistantButton>
                )}
                {/* <Survey config={erpQuizConfig} /> */}
                <QuickActionButton />
                <Tooltip title="Ajuda">
                  <div>
                    <Help />
                  </div>
                </Tooltip>
                <NPSButton />
                <ChangelogBellButton />
                <NotificationCenter
                  onOpenReportModal={() => setIsOpenModalReport(true)}
                  onOpenBalanceModal={handleOpenBalanceModal}
                />

                {/* O tooltip "Configurações" mora no MerchantAccount, preso ao
                    ícone e controlado pelo estado do menu */}
                <MerchantAccount setIsOpenModalReport={setIsOpenModalReport} />
              </S.EndIconsWrapper>
            </S.ToolbarContentWrapper>
          ) : (
            <Box
              width={'100%'}
              display="flex"
              flexDirection="column"
              alignItems="center"
              alignSelf="start"
            >
              <Stack width="100%" py={2} pt={2.5} mb={2} gap={1}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Logo id="responsive-logo" />
                  <S.Actions>
                    {/* SC-10999: fora do grupo, como no desktop */}
                    {!groupMode && (
                      <S.AssistantButton
                        variantButton="primary"
                        onClick={() => setIsAssistantModalOpen(true)}
                      >
                        <Icon
                          icon="autoAwesome"
                          colorSX={theme.palette.primary.main}
                        />
                      </S.AssistantButton>
                    )}
                    <Search />
                    <ChangelogBellButton />
                    <NotificationCenter
                      onOpenReportModal={() => setIsOpenModalReport(true)}
                      onOpenBalanceModal={handleOpenBalanceModal}
                    />
                    <IconButton onClick={handleToggleMobileMenu}>
                      <FontAwesomeIcon
                        icon={faBars}
                        style={{
                          width: 25,
                          height: 25,
                          fontSize: 22,
                          color: '#777877'
                        }}
                      />
                    </IconButton>
                  </S.Actions>
                </Box>
              </Stack>

              <MobileDropdown
                balance={balance}
                setIsOpenModalReport={setIsOpenModalReport}
                handleOpenMenuDetails={handleOpenMenuDetails}
                setIsExpandedMobileMenu={setIsExpandedMobileMenu}
                isExpandedMobileMenu={isExpandedMobileMenu}
              />
            </Box>
          )}
        </S.Toolbar>
        {/*match && isExpandedMobileMenu && <GlobalSearch />*/}
      </S.AppBarMUI>

      {isOpenModalReport && (
        <ModalReport
          handleCloseReportModal={() => setIsOpenModalReport(false)}
          isOpenModalReport={isOpenModalReport}
        />
      )}

      {modalDetailsIsOpen && (
        <Can permission="balance:view">
          <ModalBalance
            balance={balance}
            balanceScheduled={balanceScheduled}
            handleClose={handleCloseModalDetails}
            isOpen={modalDetailsIsOpen}
            initialTab={balanceInitialTab}
            initialDisputeUuid={balanceInitialDisputeUuid}
            hasActiveDisputes={hasActiveDisputes}
          />
        </Can>
      )}

      {isAssistantModalOpen && (
        <ChatAssistant
          isModalOpen={isAssistantModalOpen}
          onCloseModal={() => setIsAssistantModalOpen(false)}
        />
      )}
    </>
  )
}
export default memo(AppBar)
